import { useEffect, useRef, useState } from 'react'
import { Trash2, X } from 'lucide-react'
import type { Tag } from '#shared/types'
import type { Project } from '#shared/projects'
import { formatDateLabel } from '#shared/dates'
import { formatHours } from '#shared/duration'
import { LogTimeForm } from './LogTimeForm'

/**
 * One entry as this dialog needs it, whichever endpoint it came from.
 *
 * The Dashboard has raw entries with tag *ids*; the manager's week report has
 * them already resolved to names and carries an author. Normalising here
 * keeps one dialog rather than two that drift apart.
 */
export interface CellEntry {
  id: string
  minutes: number
  notes: string | null
  /** Tag names, already resolved — what the row reads as. */
  tags: string[]
  /** Tag ids, for the edit form. Only needed where `canEdit` is true. */
  tagIds?: string[]
  /** Who logged it. Only set when the view is showing several people. */
  who?: string
}

interface Props {
  /** The cell's project — what the dialog is headed with. */
  project: Project
  /**
   * Every project, for the picker on the form — adding and editing alike.
   *
   * Empty where nothing is editable, since a read-only dialog draws no
   * form and has nothing to offer them to.
   */
  projects: Project[]
  /** The day the cell is in. */
  date: string
  /** Today in the viewer's timezone, for the date label. */
  today: string
  /** Everything already logged in this cell. */
  entries: CellEntry[]
  /** The tags that can go on a new entry. */
  tags: Tag[]
  /**
   * False for the manager's view of someone else's week: the API scopes both
   * writing and deleting an entry to its owner, so offering either would be
   * offering something that would be refused.
   */
  canEdit: boolean
  onClose: () => void
  onChanged: () => void | Promise<void>
  /** Rejects if the delete failed, so the dialog knows not to close. */
  onDelete: (entry: CellEntry) => void | Promise<void>
}

/**
 * What a cell of the week grid opens.
 *
 * A cell is a project and a day, which is two of the four answers a time
 * entry needs — so the dialog fills those in and asks only for the rest. It
 * is the same `LogTimeForm` the Dashboard uses, rather than a second form
 * with its own idea of what is valid.
 *
 * It also lists what is already in the cell. A cell holds a total, and a
 * total is not something you can edit or delete; the entries behind it are,
 * and this is the only place they are individually visible. Clicking one
 * opens it in the same form, so changing 1.5 to 2 is an edit rather than a
 * delete and a re-add.
 *
 * The project is filled in, not fixed. A cell answered the question, but it
 * does not own the answer: noticing the wrong project with the hours already
 * typed should be a change to the form, not a dismissal and a fresh start.
 * Since a cell is one project and one day, changing it puts the entry
 * outside the cell it was opened from — which is why the dialog closes on a
 * move rather than showing a list the entry is not in.
 */
export function CellEntryDialog({
  project,
  projects,
  date,
  today,
  entries,
  tags,
  canEdit,
  onClose,
  onChanged,
  onDelete,
}: Props) {
  const ref = useRef<HTMLDialogElement>(null)

  /**
   * The entry being edited, if any.
   *
   * A cell holding exactly one entry opens straight into editing it: that is
   * the whole answer to "click the cell, change the hours", and making
   * somebody click a list of one first is a step that asks nothing. With
   * several entries there is no single one to open, so the list comes first
   * and the click that picks one is doing real work.
   */
  const [editingId, setEditingId] = useState<string | null>(
    canEdit && entries.length === 1 ? entries[0].id : null,
  )

  /**
   * Whether the add form is showing.
   *
   * An empty cell is nothing but an add form, so it starts open. A cell
   * holding several entries starts as the list: those rows are what the
   * person came to see, and a form sitting under them competes with the one
   * a row opens into. It comes out from behind a button instead.
   */
  const [adding, setAdding] = useState(entries.length === 0)

  /** The entry the form is open on, if any. */
  const openEntry = editingId ? entries.find((e) => e.id === editingId) : undefined

  /**
   * A change is done, so the dialog is done.
   *
   * Saving an edit or deleting an entry answers the question the cell was
   * opened to answer; what happened is visible in the grid behind, and
   * leaving the dialog sitting there makes the person dismiss it to see the
   * result of their own change.
   */
  function finish() {
    ref.current?.close()
  }

  /**
   * Cancel goes back to the list — unless the list was one entry, in which
   * case the dialog opened straight into the form and there is no list
   * behind it to go back to.
   */
  function cancel() {
    if (entries.length <= 1) finish()
    else setEditingId(null)
  }

  /** Close only if it worked; a failure is reported by the page behind. */
  async function remove(entry: CellEntry) {
    try {
      await onDelete(entry)
      finish()
    } catch {
      // The page owns the message — see MyWeek.
    }
  }

  // An entry that has just been deleted, or saved and refetched, must not
  // leave the form open over a row that is no longer there.
  useEffect(() => {
    if (editingId && !entries.some((e) => e.id === editingId)) setEditingId(null)
  }, [entries, editingId])

  // Emptying a cell leaves nothing to list, so the form takes over again
  // rather than leaving a button to press for the only thing left to do.
  useEffect(() => {
    if (entries.length === 0) setAdding(true)
  }, [entries.length])

  // showModal rather than the `open` attribute: it is what gives the focus
  // trap, the Esc key and the backdrop, none of which is worth rebuilding.
  //
  // It also decides where focus lands, which is why the form marks its hours
  // field instead of focusing it: showModal runs after the form has mounted
  // and would take the focus straight back to the close button.
  useEffect(() => {
    const dialog = ref.current
    if (!dialog || dialog.open) return
    dialog.showModal()
  }, [])

  // Declared after the effect above so it runs after showModal has moved the
  // focus, and keyed on the open row so switching between editing and adding
  // puts the cursor back where the typing starts.
  useEffect(() => {
    const field = ref.current?.querySelector<HTMLInputElement>('[data-autofocus]')
    field?.focus()
    // Selected, not just focused: on an entry being edited the hours are
    // already filled in, and typing should replace them rather than append.
    field?.select()
  }, [editingId, adding])

  return (
    <dialog
      ref={ref}
      className="dialog"
      onClose={onClose}
      // A click on the backdrop lands on the dialog element itself; a click
      // on anything inside it does not.
      onClick={(event) => {
        if (event.target === ref.current) ref.current?.close()
      }}
      aria-labelledby="cell-dialog-title"
    >
      <div className="dialog-body">
        <div className="dialog-head">
          <div>
            <h2 id="cell-dialog-title">{project.name}</h2>
            <p className="muted dialog-sub">{formatDateLabel(date, today)}</p>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={() => ref.current?.close()}
            aria-label="Close"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* The heading already names the project and the day; this says
            which of several entries is open, which the fields alone do not. */}
        {editingId && entries.length > 1 && (
          <p className="muted hint">Editing one of {entries.length} entries here.</p>
        )}

        {/* Hidden while a row is open: an open form is the whole dialog,
            and a list above it is the other entries competing with the one
            being changed. */}
        {entries.length > 0 && !editingId && (
          <ul className="rows dialog-entries">
            {entries.map((entry) => (
              <li key={entry.id} className="row">
                {canEdit ? (
                  /* The whole summary is the target rather than a pencil at
                     the end: there is nothing else to click on the row, and
                     a four-character hours label is a poor hit area. */
                  <button
                    type="button"
                    className="row-body row-open"
                    onClick={() => setEditingId(entry.id)}
                    aria-label={`Edit ${formatHours(entry.minutes)} on ${project.name}`}
                  >
                    {summary(entry)}
                  </button>
                ) : (
                  <div className="row-body">{summary(entry)}</div>
                )}

                {canEdit && (
                  <button
                    type="button"
                    className="icon-btn icon-danger"
                    onClick={() => remove(entry)}
                    aria-label={`Delete ${formatHours(entry.minutes)} on ${project.name}`}
                    title="Delete entry"
                  >
                    <Trash2 size={16} aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* On its own, not inside the row it came from: the form is the
            whole dialog and is laid out exactly like the one on the
            Dashboard, rather than squeezed into a list item. */}
        {openEntry && (
          <LogTimeForm
            date={date}
            // Every project, and no lock: this form may move the entry.
            projects={projects.length ? projects : [project]}
            tags={tags}
            heading={false}
            autoFocus
            editing={{
              id: openEntry.id,
              projectId: project.id,
              minutes: openEntry.minutes,
              tagIds: openEntry.tagIds ?? [],
              notes: openEntry.notes,
            }}
            onCancel={cancel}
            onDelete={() => remove(openEntry)}
            onLogged={async () => {
              await onChanged()
              finish()
            }}
          />
        )}

        {entries.length === 0 && !canEdit && (
          <p className="empty">Nothing logged here.</p>
        )}

        {/* Behind a button, not open: the rows above are what the person
            came to see, and a second form under them would compete with the
            one a row opens into. Hidden entirely while a row IS open, so
            there is one form on screen and no question about which Save is
            which. */}
        {canEdit && !editingId && !adding && (
          <button type="button" className="btn-ghost btn-add" onClick={() => setAdding(true)}>
            + Add new entry for {project.name}
          </button>
        )}

        {canEdit && !editingId && adding && (
          <LogTimeForm
            date={date}
            // Every project, with the cell's one filled in. The cell
            // answered the question but does not own the answer: noticing
            // the wrong project with the hours already typed should be a
            // change to this form, not a dismissal and a fresh start.
            projects={projects.length ? projects : [project]}
            tags={tags}
            defaultProjectId={project.id}
            heading={false}
            autoFocus
            onLogged={async (loggedProjectId) => {
              // An entry logged against another project is not in this
              // cell, so there is no list here that would show it and
              // nothing left for the dialog to say.
              if (loggedProjectId !== project.id) {
                await onChanged()
                finish()
                return
              }
              // Back to the list, so the entry just added is visible as a
              // row rather than behind the form that made it.
              if (entries.length > 0) setAdding(false)
              await onChanged()
            }}
          />
        )}
      </div>
    </dialog>
  )
}

/** The read state of a row, shared by the clickable and the plain version. */
function summary(entry: CellEntry) {
  return (
    <>
      <span className="row-name">{formatHours(entry.minutes)}</span>
      {entry.tags.length > 0 && (
        <span className="tag-list">
          {entry.tags.map((name) => (
            <span key={name} className="tag-badge">
              {name}
            </span>
          ))}
        </span>
      )}
      {(entry.who || entry.notes) && (
        <span className="row-desc note">
          {entry.who && <strong className="who">{entry.who}</strong>}
          {entry.who && entry.notes && ' · '}
          {entry.notes}
        </span>
      )}
    </>
  )
}
